import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { getTasks } from 'store/modules/ai_management_store/actions';
import { taskColor } from 'services/task_appearance';
import Calendar from 'components/molecules/calendar';
import AiManagementTaskDrawer from 'components/organisms/ai_management_task_drawer';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  className: string;
  description: string;
  type: string;
  bidding_id?: number;
}

const AiManagementTasksCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const { tasks, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );

  useEffect(() => {
    dispatch(getTasks());
  }, [dispatch]);

  const [openTaskId, setOpenTaskId] = useState<number | null>(null);

  const allTasks = useMemo(() => Object.values(tasks).flat(), [tasks]);

  const openTask = allTasks.find((task) => task.id === openTaskId) ?? null;

  const events = useMemo<CalendarEvent[]>(
    () =>
      allTasks.flatMap((task): CalendarEvent[] => {
        if (!task.deadline) return [];
        const finished = Boolean(task.finished_at) || task.status === 'done';
        const localDeadline = moment.parseZone(task.deadline).format('YYYY-MM-DDTHH:mm:ss');

        return [
          {
            id: String(task.id),
            title: `${task.title} · ${task.bidding.notice_number}`,
            start: moment(localDeadline).toDate(),
            className: taskColor(localDeadline, finished),
            description: `${task.title} · ${task.bidding.notice_number} - ${task.bidding.public_body}`,
            type: 'task',
            bidding_id: task.bidding.id,
          },
        ];
      }),
    [allTasks],
  );

  if (!request.tasks.loaded && !request.tasks.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.tasks.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as tarefas.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getTasks())}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="row">
      <Calendar eventsList={events} onTaskClick={(eventId) => setOpenTaskId(Number(eventId))} />
      {openTask && <AiManagementTaskDrawer task={openTask} onClose={() => setOpenTaskId(null)} />}
    </div>
  );
};

export default AiManagementTasksCalendar;
